import { useState } from 'react';

export default function ContactSection() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('phone_alt') as string || '').trim();
    if (honeypotVal) {
      setFormSuccess(true);
      setSubmitting(false);
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9sm3f5lbrt1qcgaj5v0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      const code = (parsed?.code as string) || '';
      const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
        || (parsed?.message as string)
        || responseText || '';

      if (response.ok && code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
    }
    setSubmitting(false);
  };

  const contactInfo = [
    { icon: 'ri-phone-line', label: '客服热线', value: '400-888-9999' },
    { icon: 'ri-mail-line', label: '电子邮箱', value: 'support@moguvideo.com' },
    { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区望京科技园A座' },
    { icon: 'ri-time-line', label: '工作时间', value: '周一至周日 9:00 - 21:00' },
  ];

  return (
    <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
            联系我们
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3" style={{ fontFamily: "var(--font-heading)" }}>
            联系蘑菇视频
          </h2>
          <p className="text-sm text-foreground-500">
            蘑菇视频官网客服团队随时为您服务，欢迎通过以下方式联系我们
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {contactInfo.map((item) => (
                <div key={item.label} className="flex items-start gap-3 p-4 rounded-xl bg-background-100">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-lg text-primary-600`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                    <p className="text-sm font-semibold text-foreground-900">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="w-full aspect-video rounded-xl overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3061.1211150227745!2d116.4704388!3d39.904119199999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x35f052e450551a8f%3A0x64e7512b7f28c41e!2z5YyX5Lqs5biC5pyd6Ziz5Yy65pyb5byA5Zut!5e0!3m2!1szh-CN!2sus!4v1690000000000!5m2!1szh-CN!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="蘑菇视频公司位置"
                aria-label="蘑菇视频公司位置地图"
              ></iframe>
            </div>
          </div>

          <div>
            <form
              id="contact-form"
              data-readdy-form=""
              onSubmit={handleSubmit}
              className="p-6 rounded-xl bg-background-100"
            >
              <h3 className="text-lg font-semibold text-foreground-900 mb-5">发送消息</h3>

              {formSuccess && (
                <div className="mb-5 p-3 rounded-lg bg-accent-100 text-accent-800 text-sm">
                  <i className="ri-checkbox-circle-line mr-1.5"></i>
                  消息已发送成功！我们的客服团队将在24小时内与您联系。
                </div>
              )}
              {formError && (
                <div className="mb-5 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {formError}
                </div>
              )}

              <div className="flex flex-col gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的消息（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                </div>

                <div className="hp-field" aria-hidden="true">
                  <label htmlFor="phone_alt">备用联系方式</label>
                  <input
                    id="phone_alt"
                    name="phone_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {submitting ? '发送中...' : '发送消息'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .hp-field {
          position: absolute;
          left: -9999px;
          opacity: 0;
          height: 0;
          width: 0;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}